import PropTypes from 'prop-types'
import React from 'react'
import TextField from 'ui/molecules/TextField'
import { hasText } from 'utils/helpers'
import { ssnSchema } from 'utils/schemas'
import { ZentakeContext } from 'utils/ZentakeContext'

import { TriggerUserActions } from '@/components/TriggerUserActions'

import ZentakeFormContext from '../ZentakeFormContext'

export function validateSSNQuestion(
  questionId,
  value,
  setAnswerErrors,
  isRequired
) {
  try {
    if (isRequired && !hasText(value)) return false
    if (hasText(value)) {
      ssnSchema.validateSync(value)
      return true
    }
    return true
  } catch (error) {
    setAnswerErrors((p) => ({ ...p, [questionId]: error.message }))
    return false
  }
}

function SSNQuestion({ question, isOfficeUse, response, sessions }) {
  const { setAnswerErrors } = React.useContext(ZentakeContext)
  const formContext = React.useContext(ZentakeFormContext)
  // setAnswerErrors
  const readonly = formContext.isQuestionReadonly(question)
  const willShowNotAllowedCursor =
    formContext.willShowNotAllowedCursor(question)

  const answer = formContext.getAnswer(question)
  const value =
    answer && answer.answer_text
      ? answer.answer_text
      : question.default_value || ''

  const isDirty = formContext.isQuestionDirty(question)
  const isValid = formContext.isQuestionValid(question)
  const questionError = formContext.getQuestionError(question)

  const [hasError, setHasError] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState(
    'This field is required'
  )

  React.useEffect(() => {
    setHasError(
      (isDirty && question?.is_required && !isValid) || hasText(questionError)
    )
    setErrorMessage(
      questionError && typeof questionError === 'string'
        ? questionError
        : 'This field is required'
    )
  }, [isDirty, question.is_required, isValid, questionError])

  function handleChange(value) {
    formContext.onAnswerChanged(question, value)
  }

  const transformValue = (value) => {
    if (!value) return value
    if (value.length <= 3) return value
    if (value.length <= 5) return `${value.slice(0, 3)}-${value.slice(3)}`
    if (value.length <= 9)
      return `${value.slice(0, 3)}-${value.slice(3, 5)}-${value.slice(5)}`
    return value
  }

  const transformValueBack = (value) => {
    if (!value) return value

    return value.replace(/[^0-9]/g, '')
  }

  const validateInput = React.useCallback(async () => {
    validateSSNQuestion(
      question.id,
      transformValue(value),
      setAnswerErrors,
      question.is_required
    )
  }, [question, setAnswerErrors, value])

  return (
    <div>
      <TriggerUserActions>
        <TextField
          data-form-id={response?.form?.id}
          data-packet-id={
            sessions?.fillResponseCustomer?.packetResponse?.packet?.id
          }
          data-response-id={response?.id}
          disabled={readonly}
          errorLabel={errorMessage}
          hasError={hasError}
          isOfficeUse={isOfficeUse}
          isRequired={question.is_required}
          label={question.text}
          maxLength={11}
          onBlur={validateInput}
          onChange={(v) => handleChange(transformValueBack(v))}
          value={transformValue(value)}
          willShowNotAllowedCursor={willShowNotAllowedCursor}
        />
      </TriggerUserActions>
    </div>
  )
}

SSNQuestion.propTypes = {
  question: PropTypes.object.isRequired,
}

export default React.memo(SSNQuestion)
